import { useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, CircularProgress } from '@mui/material';
import { store } from '../store';
import { theme } from '@assets/styles/theme/muiTheme';
import { initializeAuth } from '@shared/services/auth/authInitializer';
import { tokenStorage } from '@shared/services/storage/tokenStorage';
import { authLogger } from '@shared/utils/logger';
import { checkAuthStatus } from '@features/auth/store/authSlice';

interface AppProvidersProps {
  children: React.ReactNode;
}

const AppProviders: React.FC<AppProvidersProps> = ({ children }) => {
  // Check if we have a refresh token to attempt restoration
  // If no token, we don't need to wait for initialization
  const hasRefreshToken = !!tokenStorage.getRefreshToken();
  const [isInitializing, setIsInitializing] = useState(hasRefreshToken);
  
  useEffect(() => {
    // Initialize auth silently and restore session
    const initialize = async () => {
      if (!hasRefreshToken) {
        return;
      }
      
      try {
        authLogger.log('Starting auth initialization...');
        await initializeAuth();
        authLogger.log('Auth initialization complete');
      } catch (error) {
        authLogger.error('Auth initialization failed:', error);
      } finally {
        // Always sync Redux store with actual storage state after init.
        // If init failed (expired/invalid refresh token), tokens were cleared
        // from storage but Redux still has stale isAuthenticated=true.
        // This dispatch ensures ProtectedRoute correctly redirects to /login.
        store.dispatch(checkAuthStatus());
        setIsInitializing(false);
      }
    };

    initialize();
  }, [hasRefreshToken]);

  if (isInitializing) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box 
          display="flex" 
          justifyContent="center" 
          alignItems="center" 
          minHeight="100vh"
          bgcolor="background.default"
        >
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </Provider>
  );
};

export default AppProviders;
